import _ from 'lodash';
import log from 'loglevel';
const loggers = {};
let onErrorCallBack = _.noop;
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["SILENT"] = -1] = "SILENT";
    LogLevel[LogLevel["ERROR"] = 0] = "ERROR";
    LogLevel[LogLevel["WARN"] = 1] = "WARN";
    LogLevel[LogLevel["INFO"] = 2] = "INFO";
    LogLevel[LogLevel["DEBUG"] = 3] = "DEBUG";
    LogLevel[LogLevel["TRACE"] = 4] = "TRACE";
})(LogLevel || (LogLevel = {}));
const LOG_LEVEL_DEFAULT = LogLevel.WARN;
let clientLogLevel = LOG_LEVEL_DEFAULT;
log.enableAll();
const logMethods = {
    [LogLevel.ERROR]: log.error.bind(log),
    [LogLevel.WARN]: log.warn.bind(log),
    [LogLevel.INFO]: log.info.bind(log),
    [LogLevel.DEBUG]: log.info.bind(log),
    [LogLevel.TRACE]: log.trace.bind(log),
};
const nativeLevelToLogLevelMap = {
    [LogLevel.ERROR]: ['error'],
    [LogLevel.WARN]: ['warn'],
    [LogLevel.INFO]: ['info'],
    [LogLevel.DEBUG]: ['log', 'debug'],
    [LogLevel.TRACE]: ['trace'],
};
const nativeConsoleKey = '_nativeConsole';
const getLogFromWindow = () => {
    try {
        return window[nativeConsoleKey];
    }
    catch (e) {
        return null;
    }
};
let nativeConsoleLog = getLogFromWindow() ?? {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    trace: console.trace,
    debug: console.debug,
};
export const setNativeConsoleLog = (logMethods) => {
    nativeConsoleLog = logMethods;
};
const allLogLevels = Object.keys(nativeLevelToLogLevelMap).map(Number);
const MAX_LOG_LEVEL = Math.max(...allLogLevels);
const MIN_LOG_LEVEL = Math.min(...allLogLevels);
let shouldOverrideNativeConsoleLogs = true;
function overrideNativeConsoleLog(level) {
    if (!shouldOverrideNativeConsoleLogs)
        return;
    for (let curLogLevel = level + 1; curLogLevel <= MAX_LOG_LEVEL; curLogLevel++) {
        nativeLevelToLogLevelMap[curLogLevel].forEach(nativeLogMethod => {
            console[nativeLogMethod] = _.noop;
        });
    }
    const nativeLogMethods = nativeConsoleLog;
    for (let curLogLevel = MIN_LOG_LEVEL; curLogLevel <= level; curLogLevel++) {
        nativeLevelToLogLevelMap[curLogLevel].forEach(nativeLogMethod => {
            if (nativeLogMethods[nativeLogMethod])
                console[nativeLogMethod] = nativeLogMethods[nativeLogMethod];
        });
    }
}
const ProfileMessages = {
    API_CALL_PREAMBLE: 'Api call',
    SAGE_THRIFT_CALL: 'Sage thrift call',
    SAGE_SERVER_LATENCY: 'Sage server latency',
    SAGE_RESPONSE_PROCESSING: 'Sage response processing',
    SAGE_DROPDOWN_RENDERING: 'Sage dropdown rendering',
    ANSWER_MODEL_PARSING: 'Parsing Answer Model',
    CHART_VIZ_PROCESSING_PREAMBLE: 'Processing data for Chart Viz',
    CHART_VIZ_CHART_TYPE_SUPPORT_COMPUTATION_PREAMBLE: 'Computing supported chart types for Chart Viz',
    CHART_VIZ_RENDER_PREAMBLE: 'Rendering for Highcharts Viz',
    FILTER_VIZ_PROCESSING_PREAMBLE: 'Processing data for Filter Viz',
    FILTER_VIZ_RENDER: 'Rendering for Filter Viz',
    TABLE_VIZ_PROCESSING_PREAMBLE: 'Processing data for Table Viz',
    TABLE_VIZ_RENDER_PREAMBLE: 'Rendering for Table Viz',
};
function getCurrentDateTime() {
    const d = new Date();
    return `${(d.getHours() < 10 ? '0' : '') + d.getHours()}:${d.getMinutes() < 10 ? '0' : ''}${d.getMinutes()}:${d.getSeconds() < 10 ? '0' : ''}${d.getSeconds()}.${d.getMilliseconds()}`;
}
function getCurrentErrorStack() {
    const err = new Error();
    return err.stack || '';
}
function isDebugLogEnabled() {
    return !_.isNull(clientLogLevel) && LogLevel.DEBUG <= clientLogLevel;
}
class Logger {
    constructor(name) {
        this.muted = true;
        this.everyNMap = {};
        this.msgPrefix = name ? `(${name})` : '';
    }
    isMuted() {
        return this.muted;
    }
    unmute() {
        this.muted = false;
        return this;
    }
    mute() {
        this.muted = true;
        return this;
    }
    canLog(newLogLevel) {
        if (this.muted) {
            return newLogLevel <= Math.min(newLogLevel.WARN, clientLogLevel);
        }
        const { logLevel } = this;
        if (!_.isUndefined(logLevel)) {
            return newLogLevel <= logLevel;
        }
        return newLogLevel <= clientLogLevel;
    }
    async logMessages(args, logLevel) {
        if (!this.canLog(logLevel)) {
            return;
        }
        args[0] = this.getFormattedMessage(args[0], logLevel);
        const logFn = logMethods[logLevel];
        if (logFn)
            logFn(...args);
    }
    async trace(...args) {
        this.logMessages(args, LogLevel.TRACE);
    }
    async debug(...args) {
        this.logMessages(args, LogLevel.DEBUG);
    }
    async log(...args) {
        this.debug(...args);
    }
    async info(...args) {
        this.logMessages(args, LogLevel.INFO);
    }
    async warn(...args) {
        this.logMessages(args, LogLevel.WARN);
    }
    async error(...args) {
        const stackTrace = this.privateError(args);
        onErrorCallBack(args, stackTrace);
    }
    async errorSilently(...args) {
        this.privateWarn(args);
    }
    async logException(exception) {
        if (!exception) {
            return;
        }
        this.logMessages([exception.message], LogLevel.TRACE);
    }
    everyN(logLevel, freq, msg) {
        let newLogLevel = logLevel;
        if (!this.canLog(newLogLevel) || freq < 0) {
            return;
        }
        const key = getCurrentErrorStack();
        if (!this.everyNMap.hasOwnProperty(key)) {
            this.everyNMap[key] = 0;
        }
        if (this.everyNMap[key] > 0 && Math.floor(Math.random() * freq) > 0) {
            return;
        }
        ++this.everyNMap[key];
        if (typeof newLogLevel === 'string') {
            newLogLevel = LogLevel[newLogLevel.toUpperCase()];
        }
        const args = arguments;
        for (let i = 0; i < args.length - 2; i++) {
            args[i] = args[i + 2];
        }
        args.length -= 2;
        this.logMessages(args, newLogLevel);
    }
    setLogLevel(logLevel) {
        this.logLevel = logLevel;
    }
    time(msg) {
        if (!isDebugLogEnabled() || !console || !console.time) {
            return;
        }
        console.time(msg);
    }
    timeEnd(msg) {
        if (!isDebugLogEnabled() || !console || !console.timeEnd) {
            return;
        }
        console.timeEnd(msg);
    }
    async group(msg) {
        if (!isDebugLogEnabled() || !console || !console.group) {
            return;
        }
        console.group(msg);
    }
    groupEnd() {
        if (!isDebugLogEnabled() || !console || !console.groupEnd) {
            return;
        }
        console.groupEnd();
    }
    privateError(args) {
        const stackTrace = getCurrentErrorStack();
        this.logMessages([...args, stackTrace], LogLevel.ERROR);
        return stackTrace;
    }
    privateWarn(args) {
        const stackTrace = getCurrentErrorStack();
        this.logMessages([...args, stackTrace], LogLevel.WARN);
        return stackTrace;
    }
    getFormattedMessage(msg, logLevel) {
        return (`${LogLevel[logLevel]} ${getCurrentDateTime()} ${this.msgPrefix}` +
            `(${msg ? `] ${msg}` : ''})`);
    }
}
function create(name) {
    if (!loggers[name]) {
        loggers[name] = new Logger(name);
    }
    loggers[name].unmute();
    return loggers[name];
}
function setGlobalLogLevel(level) {
    clientLogLevel = level;
    overrideNativeConsoleLog(level);
}
function registerOnErrorCallback(fn) {
    onErrorCallBack = fn;
}
function initLogger(flags) {
    const logsFromWindow = getLogFromWindow();
    if (logsFromWindow) {
        console.error = logsFromWindow.error;
        console.warn = logsFromWindow.warn;
        console.log = logsFromWindow.log;
        console.info = logsFromWindow.info;
        console.debug = logsFromWindow.debug;
        console.trace = logsFromWindow.trace;
    }
    const clientLogLevelFromFlag = LogLevel[(flags.clientLogLevel || '').toUpperCase()];
    shouldOverrideNativeConsoleLogs = flags.overrideConsoleLogs || false;
    const isDebugMode = flags.debug;
    const logLevelFromFlag = clientLogLevelFromFlag !== undefined
        ? clientLogLevelFromFlag
        : LogLevel.WARN;
    const logLevelToSet = isDebugMode ? LogLevel.DEBUG : logLevelFromFlag;
    setGlobalLogLevel(logLevelToSet);
}
export { LogLevel, create, setGlobalLogLevel, isDebugLogEnabled, ProfileMessages, registerOnErrorCallback, Logger, initLogger, };
//# sourceMappingURL=logger.js.map